import React, { useState } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Footer from '../../components/footer/FooterAdmin.js';
import Sidebar, { SidebarResponsive } from 'components/sidebar/Sidebar.js';
import ProtectedRoute from 'components/ProtectedRoute';
import { SidebarContext } from 'contexts/SidebarContext';
import { UccelliniLogo } from 'components/icons/Icons';
import ForcePasswordChange from 'components/auth/ForcePasswordChange';
import authService from 'services/authService';
import routes from 'routes.js';

const FULL_HEIGHT_PATHS = ['/appointment', '/default'];

const sidebarOffset = (collapsed) => (collapsed ? '70px' : '300px');

const MOBILE_HEADER_HEIGHT = { base: '44px', md: '52px', lg: '56px' };

export default function Dashboard(props) {
  const { ...rest } = props;
  const location = useLocation();
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(() =>
    Boolean(authService.getCurrentUser()?.mustChangePassword),
  );

  const getRoutes = (routesList) =>
    routesList.flatMap((route, key) => {
      if (route.menuOnly) {
        return [];
      }
      if (route.category) {
        return getRoutes(route.items);
      }
      if (route.layout === '/admin') {
        return (
          <Route
            path={`${route.path}`}
            element={
              <ProtectedRoute
                requiredPermissions={route.requiredPermissions}
                requiredRole={route.requiredRole}
                excludeRoles={route.excludeRoles}
                requireAny={route.requireAny}
              >
                {route.component}
              </ProtectedRoute>
            }
            key={key}
          />
        );
      }
      return [];
    });

  document.documentElement.dir = 'ltr';
  useDisclosure();

  if (mustChangePassword) {
    return (
      <ForcePasswordChange onChanged={() => setMustChangePassword(false)} />
    );
  }

  const isFullHeightView = FULL_HEIGHT_PATHS.some((p) =>
    location.pathname.includes(p),
  );
  const offset = sidebarOffset(sidebarCollapsed);

  return (
    <Box bg="gray.50" minH="100vh" h="100vh" overflow="hidden">
      <SidebarContext.Provider
        value={{
          toggleSidebar,
          setToggleSidebar,
          sidebarCollapsed,
          setSidebarCollapsed,
        }}
      >
        <Sidebar routes={routes} collapsed={sidebarCollapsed} {...rest} />
        <Flex
          display={{ base: 'flex', xl: 'none' }}
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={1200}
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
          px={{ base: 2, md: 3 }}
          minH={MOBILE_HEADER_HEIGHT}
          h={MOBILE_HEADER_HEIGHT}
          alignItems="center"
          gap={2}
          boxShadow="sm"
        >
          <SidebarResponsive
            routes={routes}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
          />
          <Box flex="1" minW={0} overflow="hidden" display="flex" alignItems="center">
            <UccelliniLogo
              variant="compact"
              h={{ base: '18px', md: '24px', lg: '26px' }}
              maxW={{ base: '108px', md: '150px', lg: '165px' }}
              w="auto"
            />
          </Box>
        </Flex>
        <Box
          ml={{ base: 0, xl: offset }}
          pt={{ base: MOBILE_HEADER_HEIGHT.base, md: MOBILE_HEADER_HEIGHT.md, lg: MOBILE_HEADER_HEIGHT.lg, xl: 0 }}
          w={{ base: '100%', xl: `calc(100% - ${offset})` }}
          maxW={{ base: '100%', xl: `calc(100% - ${offset})` }}
          h="100vh"
          minH="100vh"
          minW={0}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          transition="margin 0.3s ease, width 0.3s ease"
        >
          <Box
            flex="1"
            minH={0}
            minW={0}
            w="100%"
            maxW="100%"
            overflow={isFullHeightView ? 'hidden' : 'auto'}
        p={isFullHeightView ? { base: '6px', md: '10px' } : { base: '6px', md: '10px' }}
        pb={isFullHeightView ? { base: '6px', md: '10px' } : '8px'}
          >
            <Box
              w="100%"
              maxW="100%"
              minW={0}
              h={isFullHeightView ? '100%' : undefined}
              minH={isFullHeightView ? 0 : undefined}
            >
              <Routes>
                {getRoutes(routes)}
                <Route
                  path="/"
                  element={<Navigate to="/admin/default" replace />}
                />
              </Routes>
            </Box>
          </Box>

          {!isFullHeightView && (
            <Box flexShrink={0} w="100%" px={{ base: '8px', md: '10px' }}>
              <Footer />
            </Box>
          )}
        </Box>
      </SidebarContext.Provider>
    </Box>
  );
}
